<template>
  <div class="space-y-6">
    <div class="relative bg-white">
      <div>
        <h2 class="text-xl font-semibold text-zinc-900">
          User Permissions
        </h2>
        <p class="mt-1 text-sm text-zinc-500">
          Invite staff and control exactly what they can access in this creator account.
        </p>
      </div>

      <div
        v-if="errorMessage"
        class="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="successMessage"
        class="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"
      >
        {{ successMessage }}
      </div>

      <div
        v-if="pendingInvites.length > 0"
        class="mt-6 rounded-2xl border border-zinc-200"
      >
        <div class="border-b border-zinc-200 px-4 py-3">
          <h3 class="text-lg font-semibold text-zinc-900">
            Pending Invitations
          </h3>
          <p class="text-sm text-zinc-500">
            Invites waiting for acceptance.
          </p>
        </div>
        <div class="divide-y divide-zinc-100">
          <div
            v-for="invite in pendingInvites"
            :key="invite.inviteId"
            class="flex flex-wrap items-center justify-between gap-3 px-4 py-4"
          >
            <div class="min-w-[220px]">
              <p class="text-sm font-semibold text-zinc-900">
                {{ invite.invitedEmail }}
              </p>
              <p class="mt-1 text-xs text-zinc-500">
                {{ formatRole(invite.role) }} - Invited {{ formatDateTime(invite.createdAt) }}
              </p>
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                @click="openEditInviteModal(invite)"
              >
                Details
              </button>
              <button
                type="button"
                class="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                @click="revokeInvite(invite)"
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="relative mt-6 rounded-2xl border border-zinc-200">
        <div class="absolute left-0 top-0 z-10 -translate-x-1/2 -translate-y-1/2">
          <div class="group relative">
            <button
              type="button"
              class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#2d8bff] text-white shadow-lg transition hover:bg-[#1b67c2]"
              @click="openInviteModal"
            >
              <PlusIcon class="h-6 w-6" />
            </button>
            <div
              class="pointer-events-none absolute -top-11 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-zinc-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100"
            >
              Add a user
            </div>
          </div>
        </div>

        <div class="px-6 pb-3 pt-5">
          <h3 class="text-xl font-semibold text-zinc-900">
            Staff ({{ staffUsedCount }} of {{ STAFF_LIMIT }})
          </h3>
          <p class="mt-1 text-sm text-zinc-600">
            This table shows the users that have access to this account.
          </p>
        </div>

        <div class="border-y border-zinc-200 bg-white px-6 py-3">
          <div class="grid grid-cols-12 gap-3 text-xs font-semibold uppercase tracking-wide text-zinc-700">
            <div class="col-span-12 md:col-span-3">
              User
            </div>
            <div class="col-span-6 md:col-span-3">
              Last Signed In
            </div>
            <div class="col-span-6 md:col-span-2">
              Access Level
            </div>
            <div class="col-span-6 md:col-span-2">
              Added-On
            </div>
            <div class="col-span-6 md:col-span-2 text-right">
              Action
            </div>
          </div>
        </div>

        <div
          v-if="staffRows.length === 0"
          class="px-6 py-4 text-sm text-zinc-500"
        >
          No staff members yet.
        </div>
        <div
          v-else
          class="divide-y divide-zinc-200"
        >
          <div
            v-for="row in staffRows"
            :key="row.key"
            class="grid grid-cols-12 gap-3 px-6 py-3.5 text-sm text-zinc-600"
          >
            <div class="col-span-12 break-all md:col-span-3">
              {{ row.email }}
            </div>
            <div class="col-span-6 md:col-span-3">
              {{ row.lastLoginAt ? formatDateTime(row.lastLoginAt) : '-' }}
            </div>
            <div class="col-span-6 md:col-span-2">
              {{ row.isCreatorAdmin ? 'Admin' : formatRole(row.role) }}
            </div>
            <div class="col-span-6 md:col-span-2">
              {{ row.addedAt ? formatDateTime(row.addedAt) : '-' }}
            </div>
            <div class="col-span-6 text-right md:col-span-2">
              <template v-if="row.isCreatorAdmin">
                -
              </template>
              <template v-else>
                <div class="inline-flex items-center gap-3 whitespace-nowrap">
                  <button
                    type="button"
                    class="text-sm text-zinc-700 transition hover:text-zinc-900"
                    @click="openEditMemberModal(row)"
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    class="text-sm text-zinc-700 transition hover:text-red-600"
                    @click="openRemoveMember(row)"
                  >
                    Revoke
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="px-6 pb-5 pt-4 text-right text-sm font-medium text-zinc-800">
          Staff ({{ staffUsedCount }} of {{ STAFF_LIMIT }})
        </div>
      </div>
    </div>
  </div>

  <TransitionRoot
    as="template"
    :show="isModalOpen"
  >
    <Dialog
      class="relative z-[110]"
      @close="closeModal"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-zinc-900/40 backdrop-blur-[2px]" />
      </TransitionChild>

      <div class="fixed inset-0 z-[111] overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-200"
            enter-from="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="w-full max-w-4xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              <div class="flex items-start justify-between border-b border-zinc-200 px-6 py-5">
                <div>
                  <DialogTitle class="text-2xl font-semibold text-zinc-900">
                    {{ modalTitle }}
                  </DialogTitle>
                  <p class="mt-1 text-sm text-zinc-500">
                    Configure staff access for this creator account.
                  </p>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1 text-zinc-500 hover:bg-zinc-100"
                  @click="closeModal"
                >
                  <XMarkIcon class="h-6 w-6" />
                </button>
              </div>

              <div class="app-scroll max-h-[70vh] overflow-y-auto px-6 py-5">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">First Name</label>
                    <input
                      v-model="form.firstName"
                      type="text"
                      class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
                      :disabled="modalMode === 'member'"
                    >
                  </div>
                  <div>
                    <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Last Name</label>
                    <input
                      v-model="form.lastName"
                      type="text"
                      class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
                      :disabled="modalMode === 'member'"
                    >
                  </div>
                  <div class="sm:col-span-2">
                    <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Email</label>
                    <input
                      v-model="form.email"
                      type="email"
                      class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
                      :disabled="modalMode !== 'invite'"
                    >
                  </div>
                  <div class="sm:col-span-2">
                    <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Access Level</label>
                    <PlatformaSelect
                      v-model="form.role"
                      class="mt-1"
                      :options="roleOptions"
                      placeholder="Select access level"
                      :disabled="busy"
                    />
                  </div>

                  <div
                    v-if="modalMode === 'invite-edit'"
                    class="sm:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-4"
                  >
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold text-zinc-900">
                          Pending invite for {{ inviteDisplayName || form.email }}
                        </p>
                        <p class="mt-1 text-xs text-zinc-600">
                          Share this activation link directly if needed.
                        </p>
                      </div>
                      <span class="inline-flex items-center rounded-md border border-amber-300 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                        Pending
                      </span>
                    </div>

                    <div class="mt-3">
                      <label class="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Activation Link</label>
                      <div class="mt-1 flex items-center gap-2">
                        <input
                          :value="inviteActivationLink"
                          type="text"
                          class="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-xs text-zinc-700 outline-none focus:border-[#2d8bff]"
                          readonly
                        >
                        <button
                          type="button"
                          class="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-300 bg-white text-zinc-600 transition hover:bg-zinc-50 disabled:opacity-50"
                          :disabled="!inviteActivationLink || inviteActivationBusy"
                          @click="copyInviteActivationLink"
                        >
                          <ArrowPathIcon
                            v-if="inviteActivationBusy"
                            class="h-5 w-5 animate-spin"
                          />
                          <CheckCircleIcon
                            v-else-if="inviteActivationCopied"
                            class="h-5 w-5 text-emerald-500"
                          />
                          <ClipboardDocumentIcon
                            v-else
                            class="h-5 w-5"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-6">
                  <div class="flex items-center justify-between">
                    <h4 class="text-base font-semibold text-zinc-900">
                      Permissions
                    </h4>
                    <button
                      type="button"
                      class="rounded-lg border border-zinc-300 px-3 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                      @click="toggleSelectAll"
                    >
                      {{ allSelected ? 'Clear All' : 'Select All' }}
                    </button>
                  </div>
                  <p class="mt-1 text-sm text-zinc-500">
                    Choose what this user can access.
                  </p>

                  <div class="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div
                      v-for="group in permissionGroups"
                      :key="group.key"
                      class="rounded-xl border border-zinc-200 bg-zinc-50 p-4"
                    >
                      <h5 class="text-sm font-semibold text-zinc-900">
                        {{ group.label }}
                      </h5>
                      <div class="mt-3 space-y-2">
                        <div
                          v-for="perm in group.items"
                          :key="`${group.key}.${perm.key}`"
                        >
                          <label
                            class="flex items-center gap-2 text-sm text-zinc-700"
                            :class="{
                              'ml-5 text-xs text-zinc-600': group.key === 'platform' && ['memberSettingsDomains', 'memberSettingsPayments', 'memberSettingsCrypto'].includes(perm.key),
                            }"
                          >
                            <input
                              :checked="Boolean(form.permissions[group.key]?.[perm.key])"
                              type="checkbox"
                              class="h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-[#2d8bff]"
                              @change="setPermission(group.key, perm.key, $event.target.checked)"
                            >
                            {{ perm.label }}
                          </label>

                          <div
                            v-if="group.key === 'coreFeatures' && Array.isArray(perm.children) && perm.children.length"
                            class="mt-2 ml-6 space-y-1"
                          >
                            <label
                              v-for="child in perm.children"
                              :key="`${group.key}.${perm.key}.${child.key}`"
                              class="flex items-center gap-2 text-xs text-zinc-600"
                            >
                              <input
                                :checked="Boolean(form.permissions[group.key]?.[child.key])"
                                type="checkbox"
                                class="h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-[#2d8bff]"
                                @change="setPermission(group.key, child.key, $event.target.checked)"
                              >
                              {{ child.label }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex justify-end gap-3 border-t border-zinc-200 px-6 py-4">
                <button
                  type="button"
                  class="rounded-xl border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                  :disabled="busy"
                  @click="closeModal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="rounded-xl bg-[#2d8bff] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1b67c2] disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="busy"
                  @click="submitModal"
                >
                  {{ busy ? 'Saving...' : modalActionLabel }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <CreatorStaffRemoveConfirmModal
    :open="removeModalOpen"
    :busy="busy"
    :target-email="removeTarget?.email || ''"
    @close="closeRemoveModal"
    @confirm="confirmRemoveMember"
  />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  PlusIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/vue/24/outline';
import CreatorStaffRemoveConfirmModal from '@/Components/CreatorStaffRemoveConfirmModal.vue';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';
import { fetchCreatorStaffSession } from '@/utils/creatorStaffAccess';
import { emitCreatorPlanLimitPrompt } from '@/utils/creatorPlanLimitPrompt';

const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').trim().replace(/\/+$/, '');
const apiBackendUrl = String(process.env.VUE_APP_PLATFORMA_API_BACKEND_URL || '').trim().replace(/\/+$/, '');
const router = useRouter();

const isLoading = ref(false);
const busy = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const STAFF_LIMIT = 5;
const creatorAccount = ref(null);
const staffMembers = ref([]);
const pendingInvites = ref([]);
const permissionCatalog = ref({
  account: { label: 'Account Settings', items: [] },
  main: { label: 'Main Navigation', items: [] },
  engage: { label: 'Engage', items: [] },
  platform: { label: 'Online Platform', items: [] },
  coreFeatures: { label: 'Core Features', items: [] },
});

const isModalOpen = ref(false);
const modalMode = ref('invite'); // invite | invite-edit | member
const editTarget = ref(null);
const removeModalOpen = ref(false);
const removeTarget = ref(null);
const inviteActivationLink = ref('');
const inviteActivationCopied = ref(false);
const inviteActivationBusy = ref(false);
let inviteCopiedTimer = null;

const blankPermissions = () => ({
  account: Object.fromEntries((permissionCatalog.value.account?.items || []).map((perm) => [perm.key, false])),
  main: Object.fromEntries((permissionCatalog.value.main?.items || []).map((perm) => [perm.key, false])),
  engage: Object.fromEntries((permissionCatalog.value.engage?.items || []).map((perm) => [perm.key, false])),
  platform: Object.fromEntries((permissionCatalog.value.platform?.items || []).map((perm) => [perm.key, false])),
  coreFeatures: {},
});

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  role: 'staff',
  permissions: blankPermissions(),
});

const permissionGroups = computed(() => {
  const groups = [
    { key: 'account', label: permissionCatalog.value.account?.label || 'Account Settings', items: permissionCatalog.value.account?.items || [] },
    { key: 'main', label: permissionCatalog.value.main?.label || 'Main Navigation', items: permissionCatalog.value.main?.items || [] },
    { key: 'engage', label: permissionCatalog.value.engage?.label || 'Engage', items: permissionCatalog.value.engage?.items || [] },
    { key: 'platform', label: permissionCatalog.value.platform?.label || 'Online Platform', items: permissionCatalog.value.platform?.items || [] },
    { key: 'coreFeatures', label: permissionCatalog.value.coreFeatures?.label || 'Core Features', items: permissionCatalog.value.coreFeatures?.items || [] },
  ];
  return groups.filter((group) => Array.isArray(group.items) && group.items.length > 0);
});
const roleOptions = [
  { value: 'staff', label: 'Staff - Limited Permissions' },
  { value: 'admin', label: 'Admin - Full Permissions' },
];

const staffRows = computed(() => {
  const rows = [];
  if (creatorAccount.value?.email) {
    const adminName = [safeText(creatorAccount.value.firstName), safeText(creatorAccount.value.lastName)].filter(Boolean).join(' ').trim();
    rows.push({
      key: `creator-admin-${creatorAccount.value.userId || 'self'}`,
      email: safeText(creatorAccount.value.email),
      displayName: adminName || null,
      role: 'admin',
      lastLoginAt: null,
      addedAt: null,
      isCreatorAdmin: true,
      membershipId: null,
    });
  }
  for (const member of staffMembers.value) {
    rows.push({
      ...member,
      key: `staff-${member.membershipId}`,
      addedAt: member.acceptedAt || member.invitedAt || member.createdAt || null,
      isCreatorAdmin: false,
    });
  }
  return rows;
});

const staffUsedCount = computed(() => staffRows.value.length);

const allSelected = computed(() => {
  for (const group of permissionGroups.value) {
    for (const item of group.items || []) {
      if (!form.value.permissions?.[group.key]?.[item.key]) return false;
    }
  }
  return true;
});

const modalTitle = computed(() => {
  if (modalMode.value === 'member') return 'Edit Staff Access';
  if (modalMode.value === 'invite-edit') return 'Pending Invite Details';
  return 'Invite User';
});

const modalActionLabel = computed(() => {
  if (modalMode.value === 'member') return 'Save Permissions';
  if (modalMode.value === 'invite-edit') return 'Save Changes';
  return 'Send Invite';
});
const inviteDisplayName = computed(() => {
  const first = safeText(form.value.firstName);
  const last = safeText(form.value.lastName);
  return [first, last].filter(Boolean).join(' ').trim();
});

const safeText = (value) => String(value == null ? '' : value).trim();

const withApiBase = (path) => {
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return apiProxyUrl ? `${apiProxyUrl}${normalizedPath}` : normalizedPath;
};

const withBackendBase = (path) => {
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return apiBackendUrl ? `${apiBackendUrl}${normalizedPath}` : normalizedPath;
};

const parseApiPayload = async (response) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => '');
  return { __rawText: text };
};

const buildAliasPath = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/') ? String(pathValue || '') : `/${String(pathValue || '')}`;
  if (normalizedPath.startsWith('/api/creators/staff')) {
    return normalizedPath.replace('/api/creators/staff', '/api/creator-staff');
  }
  return normalizedPath;
};

const buildCandidateUrls = (normalizedPath) => {
  const unique = new Set();
  const push = (value) => {
    const text = safeText(value);
    if (!text) return;
    unique.add(text);
  };

  const aliasPath = buildAliasPath(normalizedPath);
  push(withApiBase(normalizedPath));
  push(withApiBase(aliasPath));
  push(withBackendBase(normalizedPath));
  push(withBackendBase(aliasPath));
  push(normalizedPath);
  push(aliasPath);

  return Array.from(unique);
};

const fetchStaffApi = async (path, options = {}) => {
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  const candidates = buildCandidateUrls(normalizedPath);

  let lastError = new Error('Request failed');

  for (const url of candidates) {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        ...options,
      });
      const payload = await parseApiPayload(response);
      const rawText = safeText(payload?.__rawText || '');
      const loweredRawText = rawText.toLowerCase();
      const htmlRouteError = loweredRawText.includes('<!doctype html') && loweredRawText.includes('cannot get');
      const routeNotFound = response.status === 404 || htmlRouteError;

      if (routeNotFound && url !== candidates[candidates.length - 1]) {
        continue;
      }

      if (!response.ok || payload?.success === false) {
        emitCreatorPlanLimitPrompt(payload);
        const message = payload?.message || (routeNotFound ? 'Staff route is unavailable. Please check the creator proxy/backend route.' : '') || `Request failed (${response.status})`;
        const error = new Error(message);
        if (!routeNotFound) error.stopRetry = true;
        throw error;
      }
      return payload;
    } catch (error) {
      lastError = error;
      if (error?.stopRetry) break;
    }
  }

  throw lastError;
};

const showError = (message) => {
  errorMessage.value = message;
  successMessage.value = '';
};

const showSuccess = (message) => {
  successMessage.value = message;
  errorMessage.value = '';
};

const clearMessages = () => {
  errorMessage.value = '';
  successMessage.value = '';
};

const resetForm = () => {
  form.value = {
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff',
    permissions: blankPermissions(),
  };
  inviteActivationLink.value = '';
  inviteActivationCopied.value = false;
};

const setPermission = (groupKey, permissionKey, value) => {
  if (!form.value.permissions[groupKey]) form.value.permissions[groupKey] = {};
  const nextValue = Boolean(value);
  form.value.permissions[groupKey][permissionKey] = nextValue;

  if (groupKey === 'platform' && permissionKey === 'productInstances' && !nextValue) {
    const nextCoreFeatures = { ...(form.value.permissions.coreFeatures || {}) };
    for (const [featureKey] of Object.entries(nextCoreFeatures)) {
      nextCoreFeatures[featureKey] = false;
    }
    form.value.permissions.coreFeatures = nextCoreFeatures;
    return;
  }

  if (groupKey === 'platform' && permissionKey === 'memberSettings') {
    if (!nextValue) {
      form.value.permissions.platform.memberSettingsDomains = false;
      form.value.permissions.platform.memberSettingsPayments = false;
      form.value.permissions.platform.memberSettingsCrypto = false;
    }
    return;
  }

  if (
    groupKey === 'platform' &&
    ['memberSettingsDomains', 'memberSettingsPayments', 'memberSettingsCrypto'].includes(permissionKey) &&
    nextValue
  ) {
    form.value.permissions.platform.memberSettings = true;
  }

  if (groupKey === 'platform' && permissionKey === 'themeEditor' && nextValue) {
    form.value.permissions.platform.productInstances = true;
  }

  if (groupKey === 'coreFeatures') {
    const childMatch = permissionKey.match(/^(.*)::stockroom\./);
    if (!nextValue && !childMatch) {
      const nextCoreFeatures = { ...(form.value.permissions.coreFeatures || {}) };
      for (const [featureKey] of Object.entries(nextCoreFeatures)) {
        if (String(featureKey).startsWith(`${permissionKey}::stockroom.`)) {
          nextCoreFeatures[featureKey] = false;
        }
      }
      form.value.permissions.coreFeatures = nextCoreFeatures;
    }

    if (childMatch && nextValue) {
      const parentFeatureKey = String(childMatch[1] || '').trim();
      if (parentFeatureKey) {
        form.value.permissions.coreFeatures[parentFeatureKey] = true;
      }
    }

    if (nextValue) {
      form.value.permissions.platform.productInstances = true;
    }
  }
};

const toggleSelectAll = () => {
  const nextValue = !allSelected.value;
  for (const group of permissionGroups.value) {
    for (const item of group.items || []) {
      setPermission(group.key, item.key, nextValue);
    }
  }
};

const payloadFromForm = () => ({
  firstName: safeText(form.value.firstName),
  lastName: safeText(form.value.lastName),
  email: safeText(form.value.email).toLowerCase(),
  role: safeText(form.value.role) === 'admin' ? 'admin' : 'staff',
  permissions: form.value.permissions,
});

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return 'Never';
  return parsed.toLocaleString();
};

const formatRole = (value) => (safeText(value).toLowerCase() === 'admin' ? 'Admin' : 'Staff - Limited Permissions');

const loadStaffContext = async () => {
  isLoading.value = true;
  clearMessages();
  try {
    const data = await fetchStaffApi('/api/creators/staff', { method: 'GET' });
    creatorAccount.value = data.creator || null;
    staffMembers.value = Array.isArray(data.memberships) ? data.memberships : [];
    pendingInvites.value = (Array.isArray(data.invites) ? data.invites : []).filter(
      (invite) => safeText(invite.status).toLowerCase() === 'pending'
    );
    permissionCatalog.value = data.permissionCatalog || {
      account: { label: 'Account Settings', items: [] },
      main: { label: 'Main Navigation', items: [] },
      engage: { label: 'Engage', items: [] },
      platform: { label: 'Online Platform', items: [] },
      coreFeatures: { label: 'Core Features', items: [] },
    };
    if (!form.value.permissions || !Object.keys(form.value.permissions.account || {}).length) {
      form.value.permissions = blankPermissions();
    }
  } catch (error) {
    showError(error.message || 'Failed to load staff accounts');
  } finally {
    isLoading.value = false;
  }
};

const openInviteModal = () => {
  modalMode.value = 'invite';
  editTarget.value = null;
  resetForm();
  isModalOpen.value = true;
};

const openEditInviteModal = (invite) => {
  modalMode.value = 'invite-edit';
  editTarget.value = invite;
  form.value = {
    firstName: safeText(invite.invitedFirstName),
    lastName: safeText(invite.invitedLastName),
    email: safeText(invite.invitedEmail),
    role: safeText(invite.role).toLowerCase() === 'admin' ? 'admin' : 'staff',
    permissions: invite.permissions || blankPermissions(),
  };
  isModalOpen.value = true;
  void loadInviteActivationLink(invite);
};

const openEditMemberModal = (member) => {
  modalMode.value = 'member';
  editTarget.value = member;
  form.value = {
    firstName: safeText(member.firstName),
    lastName: safeText(member.lastName),
    email: safeText(member.email),
    role: safeText(member.role).toLowerCase() === 'admin' ? 'admin' : 'staff',
    permissions: member.permissions || blankPermissions(),
  };
  isModalOpen.value = true;
};

const closeModal = () => {
  if (busy.value) return;
  isModalOpen.value = false;
  inviteActivationLink.value = '';
  inviteActivationCopied.value = false;
  if (inviteCopiedTimer) {
    clearTimeout(inviteCopiedTimer);
    inviteCopiedTimer = null;
  }
};

const loadInviteActivationLink = async (invite) => {
  const inviteId = Number(invite?.inviteId || 0);
  if (!inviteId) return;
  inviteActivationBusy.value = true;
  inviteActivationCopied.value = false;
  try {
    const data = await fetchStaffApi(`/api/creators/staff/invites/${encodeURIComponent(inviteId)}/activation-link`, {
      method: 'POST',
    });
    inviteActivationLink.value = safeText(data?.activation?.acceptUrl);
  } catch (error) {
    showError(error.message || 'Failed to load activation link');
  } finally {
    inviteActivationBusy.value = false;
  }
};

const copyInviteActivationLink = async () => {
  if (!inviteActivationLink.value || inviteActivationBusy.value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteActivationLink.value);
    } else {
      const input = document.createElement('input');
      input.value = inviteActivationLink.value;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    inviteActivationCopied.value = true;
    if (inviteCopiedTimer) clearTimeout(inviteCopiedTimer);
    inviteCopiedTimer = setTimeout(() => {
      inviteActivationCopied.value = false;
      inviteCopiedTimer = null;
    }, 1600);
  } catch (_error) {
    showError('Unable to copy activation link');
  }
};

const submitModal = async () => {
  if (busy.value) return;
  busy.value = true;
  clearMessages();
  try {
    const payload = payloadFromForm();

    let url = '';
    let method = 'POST';

    if (modalMode.value === 'invite') {
      url = '/api/creators/staff/invites';
      method = 'POST';
    } else if (modalMode.value === 'invite-edit') {
      url = `/api/creators/staff/invites/${encodeURIComponent(editTarget.value.inviteId)}`;
      method = 'PATCH';
    } else {
      url = `/api/creators/staff/memberships/${encodeURIComponent(editTarget.value.membershipId)}`;
      method = 'PATCH';
    }

    await fetchStaffApi(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    showSuccess(modalMode.value === 'invite' ? 'Invitation sent.' : 'Permissions updated.');
    isModalOpen.value = false;
    await loadStaffContext();
  } catch (error) {
    showError(error.message || 'Failed to save staff changes');
  } finally {
    busy.value = false;
  }
};

const revokeInvite = async (invite) => {
  if (busy.value) return;
  busy.value = true;
  clearMessages();
  try {
    await fetchStaffApi(`/api/creators/staff/invites/${encodeURIComponent(invite.inviteId)}/revoke`, {
      method: 'POST',
    });
    showSuccess('Invite revoked.');
    await loadStaffContext();
  } catch (error) {
    showError(error.message || 'Failed to revoke invite');
  } finally {
    busy.value = false;
  }
};

const openRemoveMember = (member) => {
  removeTarget.value = member;
  removeModalOpen.value = true;
};

const closeRemoveModal = () => {
  if (busy.value) return;
  removeModalOpen.value = false;
  removeTarget.value = null;
};

const confirmRemoveMember = async () => {
  if (!removeTarget.value || busy.value) return;
  busy.value = true;
  clearMessages();
  try {
    await fetchStaffApi(`/api/creators/staff/memberships/${encodeURIComponent(removeTarget.value.membershipId)}/remove`, {
      method: 'POST',
    });
    showSuccess('Staff access removed.');
    closeRemoveModal();
    await loadStaffContext();
  } catch (error) {
    showError(error.message || 'Failed to remove staff user');
  } finally {
    busy.value = false;
  }
};

onMounted(async () => {
  try {
    const session = await fetchCreatorStaffSession();
    if (session?.isStaff && String(session.role || '').toLowerCase() !== 'admin') {
      router.replace('/creator/account/general');
      return;
    }
  } catch (_error) {
    // fall through and let loader handle any route issues
  }
  await loadStaffContext();
});

onBeforeUnmount(() => {
  if (inviteCopiedTimer) {
    clearTimeout(inviteCopiedTimer);
    inviteCopiedTimer = null;
  }
});
</script>
